import { createTheme } from '@mui/material';
import { ThemeOptions } from '@mui/material/styles/createTheme';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import { alpha, darken, lighten } from '@mui/system';

import type {} from '../themeAugmentation';

/*
    When making changes to this file a Webpack 5 bug is encountered and HMR does not work.
    This can happen when other files change too. The following issue covers the problem.
    https://github.com/storybookjs/storybook/issues/17851
*/

enum FontWeights {
  Light = 300,
  Book = 400,
  Medium = 500,
  Demi = 600,
  Bold = 700,
}

const typographyStyles: TypographyOptions = {
  body1: {
    fontSize: '1rem',
    fontWeight: FontWeights.Book,
    lineHeight: '1.5rem',
    letterSpacing: '0.15px',
  },

  // TODO: Design has eliminated body2 in favor of caption. They are duplicate styles currently but we should consider disabling them
  /**
   * @deprecated Use `caption` style instead of `body2`
   */
  body2: {
    fontSize: '0.875rem',
    fontWeight: FontWeights.Book,
    lineHeight: '1.25rem',
    letterSpacing: '0.15px',
  },

  button: {
    fontSize: '1rem',
    fontWeight: FontWeights.Bold,
    lineHeight: '0.875rem',
    letterSpacing: '0.15px',
    textTransform: 'inherit',
  },

  caption: {
    fontSize: '0.875rem',
    fontWeight: FontWeights.Book,
    lineHeight: '1.25rem',
    letterSpacing: '0.15px',
    'b, strong': {
      fontWeight: 700,
    },
  },
  helper: {
    fontSize: '0.875rem',
    fontWeight: FontWeights.Book,
    lineHeight: '1.25rem',
    letterSpacing: '0.15px',
    'b, strong': {
      fontWeight: 700,
    },
  },

  h1: {
    fontSize: '2.375rem',
    fontWeight: FontWeights.Book,
    lineHeight: '3.25rem',
    letterSpacing: '0',
  },
  h2: {
    fontSize: '2.125rem',
    fontWeight: FontWeights.Book,
    lineHeight: '3rem',
    letterSpacing: '0',
  },
  h3: {
    fontSize: '1.85rem',
    fontWeight: FontWeights.Book,
    lineHeight: '2.625rem',
    letterSpacing: '0',
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: FontWeights.Book,
    lineHeight: '2.125rem',
    letterSpacing: '0.15px',
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: FontWeights.Book,
    lineHeight: '1.75rem',
    letterSpacing: '0.15px',
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: FontWeights.Medium,
    lineHeight: '1.75rem',
    letterSpacing: '0.15px',
  },

  overline: {
    fontSize: '0.75rem',
    fontWeight: FontWeights.Book,
    lineHeight: '2rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: FontWeights.Book,
    lineHeight: '1.75rem',
    letterSpacing: '0.15px',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: FontWeights.Demi,
    lineHeight: '1.375rem',
    letterSpacing: '0.1px',
  },
};

const white = '#FFF';

const themeOptions: ThemeOptions = {
  breakpoints: {
    values: {
      lg: 1240,
      md: 905,
      sm: 600,
      xl: 1440,
      xs: 0,
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        // If we disable this setting we need to set focus styles VIA the `.Mui-focusVisible` class
        disableFocusRipple: false,
        disableRipple: true,
        size: 'large',
      },
      styleOverrides: {
        root: ({ theme }) => {
          return {
            borderRadius: theme.spacing(4),
            '&.Mui-disabled': {
              color: theme.palette.text.secondary,
            },
          };
        },
        contained: ({ ownerState, theme }) => {
          const { color = 'primary' } = ownerState;
          const backgroundColor = color === 'inherit' ? 'inherit' : theme.palette[color].dark;
          return {
            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
            },
            '&:hover': {
              backgroundColor,
            },
            '&:focus': {
              backgroundColor,
            },
          };
        },
        outlined: ({ theme, ownerState }) => {
          const color = ownerState?.color ?? 'primary';
          const parsedColor = color === 'inherit' ? 'primary' : color;
          const paletteColor = theme.palette[parsedColor];
          if (color === 'inherit') {
            throw new Error('The color inherit is not supported for Button variant="outlined"');
          }
          const { main } = paletteColor;
          const { dark = darken(main, 0.4), light = lighten(main, 0.4), lighter = alpha(main, 0.8) } = paletteColor;
          return {
            borderColor: main,
            color: main,
            '&:hover': {
              backgroundColor: lighter,
              borderColor: main,
            },
            '&:focus': {
              backgroundColor: lighter,
              borderColor: main,
            },
            '&:active': {
              color: dark,
              backgroundColor: light,
              borderColor: dark,
            },
            '&.Mui-disabled': {
              borderColor: theme.palette.text.disabled,
            },
          };
        },
        text: ({ theme }) => ({
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary.lighter,
          },
          '&:focus': {
            backgroundColor: theme.palette.primary.lighter,
          },
          '&:active': {
            color: theme.palette.primary.dark,
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.dark,
          },
        }),
        startIcon: {
          marginLeft: '0px',
        },
        endIcon: {
          marginRight: '0px',
        },
        sizeLarge: ({ ownerState, theme }) => ({
          fontSize: '1rem',
          height: theme.spacing(6),
          paddingBottom: theme.spacing(2),
          paddingLeft: ownerState.variant === 'text' ? theme.spacing(3) : theme.spacing(4),
          paddingTop: theme.spacing(2),
          paddingRight: ownerState.variant === 'text' ? theme.spacing(3) : theme.spacing(4),
        }),
        sizeSmall: ({ theme }) => ({
          fontSize: '0.875rem',
          height: theme.spacing(4.5),
          paddingBottom: theme.spacing(1),
          paddingLeft: theme.spacing(3),
          paddingTop: theme.spacing(1),
          paddingRight: theme.spacing(3),
        }),
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableFocusRipple: false,
        disableRipple: true,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const { color = 'primary' } = ownerState;
          const parsedColor = color === 'default' ? 'primary' : color;
          return {
            color: theme.palette.text.primary,
            '&:hover': {
              color: theme.palette[parsedColor].main,
            },
            '&.Mui-checked': {
              color: theme.palette[parsedColor].main,
              '&:hover': {
                color: theme.palette[parsedColor].dark,
              },
            },
            '&.Mui-disabled': {
              color: theme.palette.text.disabled,
            },
          };
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingBottom: '16px',
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
        separator: ({ theme }) => ({
          color: theme.palette.text.disabled,
          // The Figma doc has this as 4px so half the regular space of 8.
          marginLeft: theme.spacing(0.5),
          marginRight: theme.spacing(0.5),
        }),
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            color: theme.palette.black.main,
            marginLeft: 0,
            marginRight: 0,
            marginTop: theme.spacing(1),
          };
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          ...typographyStyles.body1,
          fontWeight: FontWeights.Demi,
        }),
      },
    },
    MuiLink: {
      defaultProps: {
        fontWeight: 'bold',
        variant: 'body1',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        adornedEnd: ({ theme }) => ({
          paddingRight: theme.spacing(2),
        }),
        input: ({ theme }) => {
          return {
            // This is obnoxious, I need to set the value for lineHeight here and not inputSizeSmall.
            // The value specified in inputSizeSmall is overridden by the default MUI value for this rule.
            lineHeight: '1.5rem',
            resize: 'none',
            '&::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
          };
        },
        inputSizeSmall: {
          // while lineHeight ends up being 24px it doesn't seem to effect the inputs height.
          height: '1.5rem',
          lineHeight: '1.5rem',
        },

        adornedStart: ({ theme }) => ({
          paddingLeft: theme.spacing(2),
        }),

        root: ({ theme, ownerState }) => {
          // It's possible that ownerState can be null https://github.com/mui/material-ui/issues/31982
          const color = ownerState?.color ?? 'primary';
          const isMultiline = ownerState?.multiline ?? false;
          return {
            // Even though this is applied to the notchedOutline element we must specify the rule up here.
            // We're unable to use `&:hover` within `notchedOutline` because the pseudo selector state :hover is
            // not applied to the fieldset itself.
            '&:is(:hover, .Mui-focused) .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette[color].main,
            },
            borderRadius: '6px',
            '&:is(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:is(.MuiInputBase-colorError) .MuiOutlinedInput-notchedOutline': {
              borderWidth: '2px',
            },
            padding: isMultiline ? 0 : undefined,
          };
        },

        inputAdornedStart: ({ theme }) => ({
          paddingLeft: theme.spacing(1),
        }),
        // This slot seems to be pretty buggy. When I try and apply a border color based on the color prop
        // it seems to always choose whatever color happened to be selected in the first iteration.
        notchedOutline: ({ theme }) => {
          return {
            borderColor: theme.palette.divider,
          };
        },
      },
    },
    MuiStack: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            // Override media query based style
            [theme.breakpoints.up('sm')]: {
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            },
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
          };
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        // In MUI5 the default type is outlined.
        variant: 'standard',
      },
    },
    MuiTypography: {
      styleOverrides: {
        ...typographyStyles,
      },
    },
  },
  palette: {
    background: {
      default: '#F7F7F7',
    },
    action: {
      // gray3
      disabled: '#F0F0F0',
      disabledBackground: '#E3E3E3',
    },

    common: {
      white,
    },

    error: {
      // red2
      dark: '#C84346',
      main: '#F05A5D', // red1
    },

    black: {
      lighter: alpha('#000', 0.08),
      main: '#000',
      secondary: '#070000',
    },

    primary: {
      contrastText: white,

      lighter: '#F4FCFF',
      light: '#E5F6FC',
      mid: '#DEF3FB',
      dark: '#004D71',
      main: '#0073A8', // blue1
    },
    yellow: {
      light: '#FFEEB6',
      main: '#FFCE34',
      dark: '#EAB100',
    },
    secondary: {
      main: '#57C1E9',
    },
    divider: '#DDD',
    warning: {
      // yellow + orange/orange
      dark: '#F99F1E',
      main: '#F99F1E',
    },
    facebook: {
      contrastText: white,
      main: '#3966A2',
      dark: '#3966A2',
    },
    text: {
      disabled: '#B6B6B6',
      primary: '#1C1C1C',
      // gray1
      secondary: '#61656B',
    },
  },
  typography: {
    fontFamily: ['"Maison Neue"', '"Helvetica Neue"', 'Helvetica', 'Roboto', 'Arial', 'sans-serif'].join(','),
    fontWeightBold: 600,
    ...typographyStyles,
  },
};

const theme = createTheme(themeOptions);

export { theme as default, themeOptions };
